// src/iTwin/export/ifc/core/step.ts

/**
 * STEP-21 formatting helpers + a small writer.
 * Keep this file schema/domain agnostic: it only knows how to format values
 * and emit lines like: #12=IFCFOO(...);
 */

/** Wrapper for raw STEP tokens that must not be quoted (e.g., #12, .T., IFCBOOLEAN(.T.)) */
import { toIfcGuid } from "./guid";


export type StepRaw = { __raw: string };

/** Wrapper for STEP lists: (...) */
export type StepList = { __list: StepValue[] };

export type StepValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | StepRaw
  | StepList;

/** Create a raw STEP token (not quoted) */
export function raw(token: string): StepRaw {
  return { __raw: token };
}

/** Create a reference token like #123 */
export function ref(id: number): StepRaw {
  return raw(`#${id}`);
}

/** Create a STEP list (...) */
export function list(items: StepValue[]): StepList {
  return { __list: items };
}

/** Escape special characters inside STEP strings */
export function escapeStepString(str: string): string {
  // STEP uses doubled single-quotes inside quoted strings.
  // Backslashes are not strictly required to escape for STEP,
  // but keeping your existing behavior is fine and consistent.
  return str.replace(/'/g, "''").replace(/\\/g, "\\\\");
}

/** Format a value as STEP text */
export function fmt(value: StepValue): string {
  if (value === null || value === undefined) return "$";

  if (typeof value === "string") return `'${escapeStepString(value)}'`;
  if (typeof value === "number") {
    // Preserve integers and decimals as-is; caller controls precision.
    // Avoid NaN/Infinity in output.
    if (!Number.isFinite(value)) return "$";
    return value.toString();
  }
  if (typeof value === "boolean") return value ? ".T." : ".F.";

  if (isRaw(value)) return value.__raw;
  if (isList(value)) return `(${value.__list.map(fmt).join(",")})`;

  // Exhaustive fallback
  return `'${escapeStepString(String(value))}'`;
}

function isRaw(v: any): v is StepRaw {
  return v && typeof v === "object" && typeof v.__raw === "string";
}

function isList(v: any): v is StepList {
  return v && typeof v === "object" && Array.isArray(v.__list);
}

/**
 * ID allocator that produces numeric ids used as #<id>.
 * Keep allocator instance per export (no module-level global).
 */
export class StepIdAllocator {
  private counter = 1;

  reset(): void {
    this.counter = 1;
  }

  next(): number {
    return this.counter++;
  }
}

/**
 * Writer for DATA section lines.
 * Use addEntity() for standard entity lines and addLine() for custom lines.
 */
export class StepWriter {
  private lines: string[] = [];

  constructor(public readonly ids = new StepIdAllocator()) {}

  reset(): void {
    this.lines = [];
    this.ids.reset();
  }

  addLine(line: string): void {
    this.lines.push(line.endsWith("\n") ? line : line + "\n");
  }

  /**
   * Emit a STEP entity line: #id=NAME(arg1,arg2,...);
   * Returns the id used.
   */
  addEntity(name: string, args: StepValue[], id?: number): number {
    const useId = id ?? this.ids.next();
    const argText = args.map(fmt).join(",");
    this.lines.push(`#${useId}=${name}(${argText});\n`);
    return useId;
  }

  toString(): string {
    return this.lines.join("");
  }
}

export type NextId = () => number;

export function escapeIfcString(str: string): string {
  return String(str).replace(/'/g, "''").replace(/\\/g, "\\\\");
}

export function formatIfcValue(value: unknown): string {
  if (value === null || value === undefined) return "$";
  if (typeof value === "string") return `'${escapeIfcString(value)}'`;
  if (typeof value === "number") return Number.isFinite(value) ? value.toString() : "$";
  if (typeof value === "boolean") return value ? ".T." : ".F.";
  if (value instanceof Date) return `'${value.toISOString()}'`;
  return `'${escapeIfcString(String(value))}'`;
}

export function ifcText(v: string): string {
  return `IFCTEXT(${formatIfcValue(v)})`;
}

export function ifcInteger(n: number): string {
  if (!Number.isFinite(n)) return "IFCINTEGER(0)";
  return `IFCINTEGER(${Math.trunc(n)})`;
}

export function ifcReal(n: number): string {
  if (!Number.isFinite(n)) return "IFCREAL(0.)";
  // ensure a decimal point exists
  const s = n.toString();
  return `IFCREAL(${s.includes(".") ? s : `${s}.`})`;
}

export function ifcBoolean(b: boolean): string {
  return `IFCBOOLEAN(${b ? ".T." : ".F."})`;
}

export type IfcProp = { name: string; value: string }; // value must be an IFC-typed value, e.g. IFCTEXT('x')

export function writePropertySetForElement(args: {
  getNextId: NextId;
  ownerHistoryId?: number | null;
  elementId: number;
  psetName: string;
  props: IfcProp[];
}): string {
  const { getNextId, ownerHistoryId, elementId, psetName, props } = args;

  const filtered = props.filter(p => p.value && p.value !== "$");
  if (filtered.length === 0) return "";

  let output = "";
  const propIds: number[] = [];

  for (const p of filtered) {
    const propId = getNextId();
    propIds.push(propId);
    output += `#${propId}=IFCPROPERTYSINGLEVALUE('${escapeIfcString(p.name)}',$,${p.value},$);\n`;
  }

  const ownerHistoryRef =
    typeof ownerHistoryId === "number" && Number.isFinite(ownerHistoryId)
      ? `#${ownerHistoryId}`
      : "$";

  const psetId = getNextId();
  output += `#${psetId}=IFCPROPERTYSET('${toIfcGuid()}',${ownerHistoryRef},'${escapeIfcString(psetName)}',$,(#${propIds.join(
    ",#"
  )}));\n`;

  const relId = getNextId();
  output += `#${relId}=IFCRELDEFINESBYPROPERTIES('${toIfcGuid()}',${ownerHistoryRef},$,$,(#${elementId}),#${psetId});\n`;

  return output;
}
